import { FC, Fragment } from "react";
import { observer } from "mobx-react";
import { isFailure, isOk, isPending } from "@/common/common";
import { useDispatch, useLocaleFormat, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { Button, CloseButton, LinkButton } from "@/components-v2/Button";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DateInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Select } from "@/components-v2/Select";
import { Tabs } from "@/components-v2/Tabs";
import { TextArea } from "@/components-v2/TextArea";
import { TextInput } from "@/components-v2/TextInput";
import { FieldValue, SystemFieldValue } from "@/customFields/customFieldsComponents";
import {
  commentMaxLength,
  contactSearchPlaceholder,
  contactWarning,
  CustomFieldSelectOption,
  dateValidationEntries,
  entityLogRows,
  SystemFieldLogRow,
  systemFieldLogRows,
  valueDate,
  valueDateFieldLabel,
  valueDateLabels,
} from "@/customFields/customFieldsDomain";
import { navigate } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { getAllCountriesSortedByName } from "@/utils/countries";
import { Markdown } from "@/text/Markdown";
import {
  closeCompanyFieldDetails,
  CompanyFieldAddress,
  CompanyFieldAddressErrors,
  companyFieldCommentRequirement,
  companyFieldCompanies,
  CompanyFieldCompany,
  companyFieldCompanyLabel,
  companyFieldContacts,
  companyFieldDescription,
  companyFieldDetailRows,
  CompanyFieldDetails,
  CompanyFieldDetailsState,
  companyFieldDisplay,
  CompanyFieldEditErrors,
  companyFieldEditHelp,
  CompanyFieldEditState,
  companyFieldLabel,
  CompanyFieldLog,
  companyFieldNumberText,
  companyFieldOverlapWarning,
  companyFieldRef,
  CompanyFieldSaved,
  companyFieldSelectOptions,
  companyFieldSourceUrl,
  companyFieldTypeLabel,
  companySystemField,
  openCompanyFieldEdit,
  prepCompanyFieldEditDialog,
  prepCompanyFieldTabs,
  searchCompanyFieldCompanies,
  selectCompanyFieldCompany,
  updateCompanyFieldEdit,
  validateCompanyFieldEdit,
} from "./companyFieldDomain";

export const CompanyFieldPanel: FC = observer(() => {
  const state = useRootStore().companyFieldDetails;
  return state ? <CompanyFieldPanelView state={state} /> : null;
});

export const CompanyFieldPanelView: FC<{ state: CompanyFieldDetailsState }> = observer(
  ({ state }) => {
    const { t } = useTranslation();
    const details = isOk(state.result) ? state.result.data : undefined;
    return (
      <>
        <VBox borderBottom paddingTop={"lg"} paddingX={"md"} gap={"md"}>
          <Box justifyContent={"space-between"} alignItems={"center"} minHeight={"md"}>
            <h4>{details ? t(companyFieldLabel(details.info)) : ""}</h4>
            <CloseButton onClick={[closeCompanyFieldDetails()]} />
          </Box>
          {details && <Tabs {...prepCompanyFieldTabs(state, details)} />}
        </VBox>
        <VBox padding gap={"lg"} grow overflowY={"auto"}>
          {isPending(state.result) && <LoadingIndicator />}
          {isFailure(state.result) && <Alert type={"error"} message={t("general-error")} />}
          {details && state.tab === "field" && <CompanyFieldPanelField details={details} />}
          {details && state.tab === "log" && (
            <CompanyFieldPanelLog details={details} logResult={state.logResult} />
          )}
        </VBox>
      </>
    );
  },
);

const scopeLabels: { [key in NonNullable<CompanyFieldDetails["companyScope"]>]: TMessageKey } = {
  ALL_COMPANIES: "all-companies",
  SELECTED_COMPANIES: "entity-data.selected-companies",
  HIDDEN: "hidden",
};

const NotSet: FC = () => {
  const { t } = useTranslation();
  return <Box fg={"subtle"}>{t("entity-data.not-set")}</Box>;
};

const CompanyFieldValue: FC<{ details: CompanyFieldDetails }> = observer(({ details }) => {
  const systemField = companySystemField(details);
  return systemField ? (
    <SystemFieldValue field={systemField} />
  ) : (
    <FieldValue value={details.value} {...companyFieldDisplay(details)} />
  );
});

const CompanyFieldPanelField: FC<{ details: CompanyFieldDetails }> = observer(({ details }) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const config = details.config;
  const systemField = companySystemField(details);
  const description = companyFieldDescription(details.info);
  const detailRows = companyFieldDetailRows(details);
  const sourceUrl = companyFieldSourceUrl(details);
  return (
    <VBox gap={"lg"}>
      <Box justifyContent={"space-between"} alignItems={"start"} gap={"md"}>
        <Grid cols={2} gap={"md"} grow>
          <Box className={"font-semibold"}>{t("entity-data.current-value")}</Box>
          <CompanyFieldValue details={details} />
          {systemField && details.effectiveFrom && (
            <>
              <Box fg={"subtle"}>{t("entity-data.effective-from")}</Box>
              <div>{formatDate(details.effectiveFrom)}</div>
            </>
          )}
          {!systemField &&
            dateValidationEntries(config?.validations).map((entry) => {
              const date = valueDate(details, entry.validation);
              return (
                <Fragment key={entry.validation}>
                  <Box fg={"subtle"}>{t(valueDateLabels[entry.validation])}</Box>
                  {date ? <div>{formatDate(date)}</div> : <NotSet />}
                </Fragment>
              );
            })}
          {config?.validations.COMMENT && (
            <>
              <Box fg={"subtle"}>{t("entity-data.comment")}</Box>
              {details.comment ? <div>{details.comment}</div> : <NotSet />}
            </>
          )}
        </Grid>
        {details.canEdit && (
          <Button
            variant={"secondary"}
            icon={"edit"}
            label={{ key: "entity-data.edit-value" }}
            onClick={[
              openCompanyFieldEdit({ orgNo: details.orgNo, field: companyFieldRef(details.info) }),
            ]}
          />
        )}
      </Box>
      <Box className={"font-semibold"}>{t("entity-data.field-details")}</Box>
      <Grid cols={2} gap={"md"}>
        {details.updatedAt && (
          <>
            <Box fg={"subtle"}>{t("entity-data.last-updated")}</Box>
            <div>{formatDate(details.updatedAt)}</div>
          </>
        )}
        {details.updatedBy && (
          <>
            <Box fg={"subtle"}>{t("entity-data.last-updated-by")}</Box>
            <div>{details.updatedBy}</div>
          </>
        )}
        <Box fg={"subtle"}>{t("type")}</Box>
        <div>{t(companyFieldTypeLabel(details))}</div>
        {description && (
          <>
            <Box fg={"subtle"}>{t("entity-data.description")}</Box>
            <div>{t(description)}</div>
          </>
        )}
        {details.companyScope && (
          <>
            <Box fg={"subtle"}>{t("entity-data.activated-in")}</Box>
            <div>{t(scopeLabels[details.companyScope])}</div>
          </>
        )}
        {config?.inputType === "SINGLE_SELECT" && details.info.source === "CUSTOM" && (
          <>
            <Box fg={"subtle"}>{t("entity-data.tab-values")}</Box>
            <VBox gap={"xs"}>
              {companyFieldSelectOptions(config).map((option) => (
                <div key={option.id}>{option.label}</div>
              ))}
            </VBox>
          </>
        )}
        {detailRows.map((row) => (
          <Fragment key={row.id}>
            <Box fg={"subtle"}>{t(row.label)}</Box>
            <div>{t(row.value)}</div>
          </Fragment>
        ))}
      </Grid>
      {sourceUrl && (
        <div>
          <LinkButton
            label={{ key: "entity-data.system-detail-view-details" }}
            arrow={"right"}
            onClick={[navigate(sourceUrl)]}
          />
        </div>
      )}
    </VBox>
  );
});

const CompanyFieldPanelLog: FC<{
  details: CompanyFieldDetails;
  logResult: CompanyFieldDetailsState["logResult"];
}> = observer(({ details, logResult }) => {
  const { t } = useTranslation();
  return (
    <>
      {(logResult === undefined || isPending(logResult)) && <LoadingIndicator />}
      {isFailure(logResult) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(logResult) && <CompanyFieldLogEntries details={details} log={logResult.data} />}
    </>
  );
});

const CompanyFieldLogEntries: FC<{ details: CompanyFieldDetails; log: CompanyFieldLog }> = observer(
  ({ details, log }) => {
    const { t, lang } = useTranslation();
    const { formatDateTime } = useLocaleFormat();
    if (log.source === "SYSTEM") {
      const columns: TableColumn<SystemFieldLogRow>[] = [
        {
          name: "date",
          label: { key: "date" },
          getValue: (row) => ({ type: "date", value: row.date }),
          align: "left",
        },
        {
          name: "change",
          label: { key: "entity-data.log-change" },
          getValue: (row) => row.change,
          align: "left",
        },
        {
          name: "by",
          label: { key: "entity-data.log-by" },
          getValue: (row) => row.actorName,
          align: "left",
        },
      ];
      return (
        <DataTable
          tableColumns={columns}
          tableData={systemFieldLogRows(log.entries, lang)}
          getId={(row) => row.id}
        />
      );
    }
    return (
      <VBox gap={"md"}>
        {entityLogRows(log.entries).map((row) => (
          <Box key={row.id} gap={"md"} justifyContent={"space-between"} alignItems={"start"}>
            <VBox>
              <div>{t(row.label)}</div>
              {row.value && (
                <Box textSize={"sm"}>
                  <FieldValue value={row.value} {...companyFieldDisplay(details)} />
                </Box>
              )}
              <small className={"subtle"}>{row.actorName}</small>
            </VBox>
            <Box fg={"subtle"}>{formatDateTime(row.recordedAt)}</Box>
          </Box>
        ))}
      </VBox>
    );
  },
);

export const CompanyFieldEditDialog: FC<{ onSaved: CompanyFieldSaved }> = observer(
  ({ onSaved }) => {
    const edit = useRootStore().companyFieldEdit;
    return edit ? <CompanyFieldEditDialogView edit={edit} onSaved={onSaved} /> : null;
  },
);

export const CompanyFieldEditDialogView: FC<{
  edit: CompanyFieldEditState;
  onSaved: CompanyFieldSaved;
}> = observer(({ edit, onSaved }) => {
  const { t } = useTranslation();
  const errors = edit.showErrors ? validateCompanyFieldEdit(edit) : {};
  const warning = companyFieldOverlapWarning(edit);
  const help = companyFieldEditHelp(edit);
  const contactNote =
    edit.input.type === "CONTACT"
      ? contactWarning(edit.config, edit.input.contact ?? null, edit.details.companyName)
      : undefined;
  return (
    <Dialog {...prepCompanyFieldEditDialog(edit, onSaved)}>
      <VBox gap={"md"}>
        {help && <Markdown value={t(help)} enableLinks />}
        <Box gap={"xs"}>
          <Box fg={"subtle"}>{t("entity-data.current-value-colon")}</Box>
          <CompanyFieldValue details={edit.details} />
        </Box>
        {companyFieldDetailRows(edit.details).map((row) => (
          <Box key={row.id} gap={"xs"}>
            <Box fg={"subtle"}>{t(row.label)}:</Box>
            <div>{t(row.value)}</div>
          </Box>
        ))}
        <CompanyFieldInput edit={edit} errors={errors} />
        <CompanyFieldDates edit={edit} errors={errors} />
        <CompanyFieldComment edit={edit} error={errors.comment} />
        {contactNote && <Alert type={"warning"} icon message={contactNote} />}
        {warning && <Alert type={"warning"} icon message={warning} />}
        {isFailure(edit.submitResult) && (
          <Alert type={"error"} message={t(edit.submitResult.error)} />
        )}
      </VBox>
    </Dialog>
  );
});

const errorProps = (t: (message: TMessage) => string, error?: TMessage) => ({
  error: error !== undefined,
  message: error && t(error),
});

const CompanyFieldInput: FC<{ edit: CompanyFieldEditState; errors: CompanyFieldEditErrors }> =
  observer(({ edit, errors }) => {
    const { t, lang } = useTranslation();
    const dispatch = useDispatch();
    const label = t("entity-data.new-value");
    const error = errors.value;
    const input = edit.input;
    switch (input.type) {
      case "TEXT":
        return (
          <TextInput
            label={label}
            value={input.text}
            onChange={(event) => [
              updateCompanyFieldEdit({ input: { type: "TEXT", text: event.target.value } }),
            ]}
            {...errorProps(t, error)}
          />
        );
      case "NUMBER":
        return (
          <TextInput
            label={label}
            value={input.text}
            onChange={(event) => [
              updateCompanyFieldEdit({
                input: {
                  type: "NUMBER",
                  text: companyFieldNumberText(event.target.value, edit.config, edit.lang),
                },
              }),
            ]}
            {...errorProps(t, error)}
          />
        );
      case "DATE":
        return (
          <DateInput
            label={label}
            value={input.text}
            onInput={(event) => [
              updateCompanyFieldEdit({ input: { type: "DATE", text: event.target.value } }),
            ]}
            onChange={(params, dispatchDate) =>
              dispatchDate([
                updateCompanyFieldEdit({ input: { type: "DATE", text: params.input } }),
              ])
            }
            {...errorProps(t, error)}
          />
        );
      case "SINGLE_SELECT": {
        const options = companyFieldSelectOptions(edit.config, input.optionId);
        return (
          <Select<CustomFieldSelectOption | null>
            label={label}
            items={[null, ...options]}
            value={options.find((option) => option.id === input.optionId) ?? null}
            idFn={(option) => option?.id ?? "not-set"}
            labelFn={(option) => option?.label ?? { key: "entity-data.not-set" }}
            onSelect={(option) => [
              updateCompanyFieldEdit({ input: { type: "SINGLE_SELECT", optionId: option?.id } }),
            ]}
            fullWidth
          />
        );
      }
      case "COUNTRY_CODE": {
        const countries = getAllCountriesSortedByName(lang);
        return (
          <Select<{ code: string; name: string } | null>
            label={label}
            items={[null, ...countries]}
            value={countries.find((country) => country.code === input.code) ?? null}
            idFn={(country) => country?.code ?? "not-set"}
            labelFn={(country) => country?.name ?? { key: "entity-data.not-set" }}
            onSelect={(country) => [
              updateCompanyFieldEdit({ input: { type: "COUNTRY_CODE", code: country?.code } }),
            ]}
            fullWidth
          />
        );
      }
      case "CONTACT":
        return (
          <Autocomplete
            label={label}
            placeholder={t(contactSearchPlaceholder(edit.config))}
            items={companyFieldContacts(input)}
            value={input.contact ?? null}
            loading={isPending(input.candidates)}
            idFn={(contact) => String(contact.userId)}
            labelFn={(contact) => contact.name}
            onSelect={(contact) =>
              dispatch([updateCompanyFieldEdit({ input: { ...input, contact } })])
            }
            onClear={() =>
              dispatch([updateCompanyFieldEdit({ input: { ...input, contact: undefined } })])
            }
            fullWidth
          />
        );
      case "COMPANY":
        return (
          <Autocomplete<CompanyFieldCompany>
            label={label}
            placeholder={t("entity-data.company-search")}
            items={companyFieldCompanies(input)}
            value={input.company ?? null}
            inputValue={input.query}
            loading={isPending(input.candidates)}
            idFn={(company) => company.orgNo}
            labelFn={companyFieldCompanyLabel}
            onInputValueChange={(query) => dispatch(searchCompanyFieldCompanies(query))}
            onSelect={(company) => dispatch(selectCompanyFieldCompany(company))}
            onClear={() => dispatch(selectCompanyFieldCompany(undefined))}
            fullWidth
            {...errorProps(t, error)}
          />
        );
      case "ADDRESS":
        return <CompanyFieldAddressInput address={input.address} errors={errors.address} />;
    }
  });

const addressTextParts: {
  part: Exclude<keyof CompanyFieldAddress, "country">;
  label: TMessageKey;
}[] = [
  { part: "addressLine1", label: "settings.company.addressLine1" },
  { part: "addressLine2", label: "settings.company.addressLine2" },
  { part: "postalCode", label: "settings.updateCompanyPostalCode" },
  { part: "postalPlace", label: "settings.updateCompanyPostalPlace" },
];

const CompanyFieldAddressInput: FC<{
  address: CompanyFieldAddress;
  errors?: CompanyFieldAddressErrors;
}> = observer(({ address, errors }) => {
  const { t, lang } = useTranslation();
  const countries = getAllCountriesSortedByName(lang);
  const set = (partial: Partial<CompanyFieldAddress>) =>
    updateCompanyFieldEdit({ input: { type: "ADDRESS", address: { ...address, ...partial } } });
  return (
    <>
      {addressTextParts.map(({ part, label }) => (
        <TextInput
          key={part}
          label={t(label)}
          value={address[part]}
          onChange={(event) => [set({ [part]: event.target.value })]}
          {...errorProps(t, errors?.[part])}
        />
      ))}
      <Select<{ code: string; name: string } | null>
        label={t("settings.updateCompanyCountry")}
        items={[null, ...countries]}
        value={countries.find((country) => country.code === address.country) ?? null}
        idFn={(country) => country?.code ?? "not-set"}
        labelFn={(country) => country?.name ?? { key: "entity-data.not-set" }}
        onSelect={(country) => [set({ country: country?.code ?? "" })]}
        fullWidth
        {...errorProps(t, errors?.country)}
      />
    </>
  );
});

const CompanyFieldDates: FC<{ edit: CompanyFieldEditState; errors: CompanyFieldEditErrors }> =
  observer(({ edit, errors }) => {
    const { t } = useTranslation();
    const entries = dateValidationEntries(edit.config.validations);
    if (entries.length === 0) {
      return null;
    }
    return (
      <Box gap={"md"}>
        {entries.map((entry) => {
          const isFrom = entry.validation === "EFFECTIVE_FROM_DATE";
          const set = (text: string) =>
            updateCompanyFieldEdit(isFrom ? { effectiveFrom: text } : { validThrough: text });
          return (
            <Box key={entry.validation} flex={"auto"}>
              <DateInput
                label={t(valueDateFieldLabel(entry))}
                value={isFrom ? edit.effectiveFrom : edit.validThrough}
                onInput={(event) => [set(event.target.value)]}
                onChange={(params, dispatchDate) => dispatchDate([set(params.input)])}
                {...errorProps(t, isFrom ? errors.effectiveFrom : errors.validThrough)}
              />
            </Box>
          );
        })}
      </Box>
    );
  });

const CompanyFieldComment: FC<{ edit: CompanyFieldEditState; error?: TMessage }> = observer(
  ({ edit, error }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    if (!edit.config.validations.COMMENT) {
      return null;
    }
    return (
      <TextArea
        label={t("entity-data.comment")}
        value={edit.comment}
        placeholder={t(companyFieldCommentRequirement(edit))}
        maxLength={commentMaxLength}
        onChange={(event) => dispatch([updateCompanyFieldEdit({ comment: event.target.value })])}
        {...errorProps(t, error)}
      />
    );
  },
);
